import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading, Reveal, Placeholder } from "@/components/primitives";
import { getContent, getSettings, api, trackEvent } from "@/lib/api";
import { toast } from "sonner";
import { FileText, ShieldCheck, Globe, LinkSimple, Copy, ArrowSquareOut, Spinner } from "@phosphor-icons/react";

export default function Compliance() {
  const [legal, setLegal] = useState([]);
  const [settings, setSettings] = useState(null);
  const [active, setActive] = useState(null);
  const [feed, setFeed] = useState(null); // {valid, transactions, explorer, reason}
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    trackEvent("page_view", { page: "compliance" });
    getContent("legal").then((d) => {
      setLegal(d);
      setActive(d[0] || null);
    }).catch(() => {});
    getSettings().then(setSettings).catch(() => {});
  }, []);

  const wallets = settings?.wallet_addresses || [];

  const copy = (t) => {
    navigator.clipboard?.writeText(t);
    toast.success("Copied");
  };

  const verify = async () => {
    trackEvent("cta_click", { cta: "verify_onchain" });
    const addr = wallets[0]?.address || "";
    setVerifying(true);
    try {
      const { data } = await api.get(`/onchain/${encodeURIComponent(addr)}`);
      setFeed(data);
      if (!data.valid) {
        toast.info("No verified wallet address configured yet. Add one in the admin settings to enable live verification.");
      } else if (data.explorer) {
        window.open(data.explorer, "_blank", "noopener");
      }
    } catch {
      toast.error("Could not reach the blockchain explorer.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Compliance Center"
        title="Transparency, compliance, and verifiable activity."
        sub="Quantum Scalp is committed to operating within applicable legal and regulatory requirements and continuously reviews its structure as it expands into new jurisdictions."
      />

      {/* Transparency / On-chain */}
      <Section grid>
        <SectionHeading
          eyebrow="Transparency"
          title="Don't Take Our Word For It."
          sub="Where applicable, blockchain-based activity provides independently verifiable transaction records. We publish verified wallet addresses and transaction references — we never invent them."
          className="mb-10"
        />
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {wallets.map((w, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="rounded-xl border border-white/10 bg-[#0A0B10] p-5" data-testid={`wallet-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-white">{w.label}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#0E9E90]">{w.chain}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2.5">
                    <code className="font-mono text-xs text-[#94A3B8] truncate">{w.address}</code>
                    <button onClick={() => copy(w.address)} className="shrink-0 text-[#94A3B8] hover:text-[#2DD4BF] transition-colors" aria-label="Copy">
                      <Copy size={15} />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="h-full rounded-xl border border-[#2DD4BF]/25 bg-[#2DD4BF]/[0.05] p-7 flex flex-col justify-center">
              <LinkSimple size={30} className="text-[#2DD4BF] mb-4" weight="duotone" />
              <h3 className="text-xl font-bold text-white mb-2">Verify On-Chain Activity</h3>
              <p className="text-sm text-[#94A3B8] mb-5">
                Public blockchain transactions, wallet addresses, transaction hashes, trade history, and exportable
                reports — verifiable wherever technically possible.
              </p>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); verify(); }}
                data-testid="verify-onchain-btn"
                className="inline-flex w-max items-center gap-2 px-5 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors"
              >
                {verifying ? <><Spinner size={16} className="animate-spin" /> Verifying…</> : <><LinkSimple size={16} weight="bold" /> Verify On-Chain Activity</>}
              </a>
              <p className="mt-4"><Placeholder>[INSERT VERIFIED WALLET ADDRESS]</Placeholder></p>
            </div>
          </Reveal>
        </div>

        {feed && (
          <Reveal>
            <div className="mt-6 rounded-xl border border-white/10 bg-[#0A0B10] p-6" data-testid="onchain-feed">
              {!feed.valid ? (
                <p className="text-sm text-[#94A3B8]">{feed.reason} Live verification activates automatically once a verified wallet address is added.</p>
              ) : feed.transactions.length === 0 ? (
                <p className="text-sm text-[#94A3B8]">No recent transactions found for this address on the block explorer.</p>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-white">Recent On-Chain Transactions</h4>
                    <a href={feed.explorer} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#2DD4BF] hover:text-white transition-colors">
                      View on Etherscan <ArrowSquareOut size={13} />
                    </a>
                  </div>
                  <div className="space-y-2">
                    {feed.transactions.map((t) => (
                      <a key={t.hash} href={t.explorer} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-4 py-2.5 hover:border-[#2DD4BF]/30 transition-colors">
                        <code className="font-mono text-xs text-[#2DD4BF] truncate max-w-[40%]">{t.hash}</code>
                        <span className="font-mono text-xs text-[#94A3B8] truncate hidden sm:block">{t.method || "transfer"}</span>
                        <span className="font-mono text-xs text-white shrink-0">{t.value_eth} ETH</span>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Reveal>
        )}
      </Section>

      {/* Legal documents */}
      <Section grid>
        <SectionHeading eyebrow="Legal Documents" title="The documents that govern the platform." className="mb-10" />
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="space-y-2" data-testid="legal-list">
            {legal.map((d) => (
              <button
                key={d.id}
                onClick={() => setActive(d)}
                data-testid={`legal-${d.slug}`}
                className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
                  active?.id === d.id ? "border-[#2DD4BF]/40 bg-[#2DD4BF]/10 text-white" : "border-white/10 bg-[#0A0B10] text-[#94A3B8] hover:text-white"
                }`}
              >
                <FileText size={16} className="text-[#0E9E90]" /> {d.title}
              </button>
            ))}
          </div>
          <div className="lg:col-span-2 rounded-xl border border-white/10 bg-[#0A0B10] p-7 min-h-[280px]">
            {active && (
              <>
                <h3 className="text-xl font-bold text-white mb-4">{active.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed whitespace-pre-line">{active.body}</p>
              </>
            )}
          </div>
        </div>
      </Section>

      {/* Jurisdiction */}
      <Section grid>
        <div className="grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-xl border border-white/10 bg-[#0A0B10] p-7">
              <Globe size={28} className="text-[#2DD4BF] mb-4" weight="duotone" />
              <h3 className="text-xl font-bold text-white mb-3">United States / International Expansion</h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed">
                Quantum Scalp operates internationally and takes jurisdiction-specific compliance seriously.
                Different jurisdictions may have different rules concerning crypto, software, automated trading,
                referral programs, marketing, payments, and digital assets. Users should only access the platform
                where legally permitted.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-xl border border-white/10 bg-[#0A0B10] p-7">
              <ShieldCheck size={28} className="text-[#2DD4BF] mb-4" weight="duotone" />
              <h3 className="text-xl font-bold text-white mb-3">What We Do Not Claim</h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed mb-4">
                We do not claim to be "fully regulated globally", "SEC approved", "EU approved", or "licensed
                everywhere" unless verified by counsel.
              </p>
              <Placeholder>[INSERT VERIFIED JURISDICTION LIST]</Placeholder>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
